import { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useMetaData, useApi } from '../hooks/useApi';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { ThemeContext } from '../context/ThemeContext';
import { Geo } from '../utils/api';
import { LineChart, ChartCard, ChartLoader, ChartError } from '../components/Charts';
import { PALETTE, fmtNum, fmtMonth } from '../utils/theme';
import { geoName } from '../utils/postcodes';
import { UK_MAP_NAME as MAP_NAME, POSTCODE_REGION, REGION_POSTCODES, loadUKMap } from '../utils/ukMap';

function buildGeoSeries(rows, valueKey) {
  const geos   = [...new Set(rows.map(r => r.geography))];
  const months = [...new Set(rows.map(r => r.month_id))].sort();
  // Latest non-null value drives ranking so the legend + tooltip follow chart levels
  const latestOf = data => { for (let j = data.length - 1; j >= 0; j--) if (data[j] != null) return data[j]; return 0; };
  const series = geos
    .map(geo => {
      const byMonth = Object.fromEntries(
        rows.filter(r => r.geography === geo).map(r => [r.month_id, r[valueKey]])
      );
      const data = months.map(m => byMonth[m] ?? null);
      return { name: geoName(geo), data, latest: latestOf(data) };
    })
    .sort((a, b) => b.latest - a.latest)             // highest level first
    .map((s, i) => ({ name: s.name, color: PALETTE[i % PALETTE.length], data: s.data }));
  return { months, series };
}

export default function TrendsGeoView() {
  const { dark }       = useContext(ThemeContext);
  const { category }   = useCategory();
  const { month }      = useMonth();
  const { sessionTypes, latestMonth, loading: metaLoading } = useMetaData();

  const [sessionType,    setSessionType]    = useState('open_app');
  const [mapReady,       setMapReady]       = useState(false);
  const [geoError,       setGeoError]       = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('London');

  const effectiveCat  = category || 'ALL';
  const selectedMonth = month || latestMonth;

  useEffect(() => { loadUKMap(ok => { setMapReady(ok); setGeoError(!ok); }); }, []);

  // Snapshot for map coloring (single month, all postcodes)
  const snapshot = useApi(
    Geo.snapshot,
    { category: effectiveCat, session_type: sessionType, month_id: selectedMonth, limit: 0 },
    !!selectedMonth,
  );

  // Full 6-month trend data (all postcodes, filter client-side by region)
  const trendApi = useApi(
    () => Geo.trend({ category: effectiveCat, session_type: sessionType, month_id: selectedMonth, limit: 0 }),
    { effectiveCat, sessionType, selectedMonth },
    !!selectedMonth,
  );

  // Aggregate snapshot → region totals for map coloring
  const { regionData, maxRegionVal } = useMemo(() => {
    if (!snapshot.data) return { regionData: [], maxRegionVal: 1 };
    const valid = snapshot.data.filter(r => r.geography && r.geography.toLowerCase() !== 'unknown');
    const totals = {};
    valid.forEach(r => {
      const region = POSTCODE_REGION[r.geography];
      if (region) totals[region] = (totals[region] || 0) + (r.active_users || 0);
    });
    const regionData = Object.entries(totals).map(([name, value]) => ({ name, value }));
    const maxRegionVal = Math.max(1, ...regionData.map(d => d.value));
    return { regionData, maxRegionVal };
  }, [snapshot.data]);

  // Filter trend rows to the selected region's postcodes
  const filteredRows = useMemo(() => {
    const rows = trendApi.data || [];
    if (!selectedRegion) return rows;
    const codes = new Set(REGION_POSTCODES[selectedRegion] || []);
    return rows.filter(r => codes.has(r.geography));
  }, [trendApi.data, selectedRegion]);

  const { months: chartMonths, series: userSeries } = useMemo(
    () => filteredRows.length ? buildGeoSeries(filteredRows, 'active_users') : { months: [], series: [] },
    [filteredRows]
  );
  const { series: sessionSeries } = useMemo(
    () => filteredRows.length ? buildGeoSeries(filteredRows, 'total_15min_sessions') : { months: [], series: [] },
    [filteredRows]
  );

  // Legend can wrap onto many rows for dense regions (London ≈ 20 postcodes).
  // Estimate rows from the series count (~4 items/row) so we reserve enough space
  // below the axis and grow the chart height to keep the plotting area readable.
  const trendCount     = Math.max(userSeries.length, sessionSeries.length);
  const trendLegendRows = Math.min(7, Math.ceil((trendCount || 1) / 4) + 1);
  const trendHeight     = 200 + trendLegendRows * 26;

  const mapOption = useMemo(() => {
    if (!mapReady) return null;
    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: p => `<b>${p.name}</b><br/>Active users: <b>${fmtNum(p.value || 0)}</b>`,
        backgroundColor: dark ? '#1e2633' : '#fff',
        borderColor: dark ? '#2d3748' : '#E5E7EB',
        borderWidth: 1,
        textStyle: { color: dark ? '#F9FAFB' : '#111827', fontSize: 12 },
        extraCssText: 'box-shadow:0 4px 12px rgba(0,0,0,0.12);border-radius:8px;',
      },
      visualMap: {
        min: 0,
        max: maxRegionVal,
        text: ['High', 'Low'],
        realtime: false,
        calculable: true,
        inRange: { color: ['#E1F5EE', '#5DCAA5', '#1DB89A', '#0F6E56'] },
        textStyle: { color: dark ? '#9CA3AF' : '#6B7280', fontSize: 10 },
        right: 8,
        top: 'middle',
        itemWidth: 12,
        itemHeight: 100,
      },
      series: [{
        type: 'map',
        map: MAP_NAME,
        roam: true,
        data: regionData,
        selectedMode: 'single',
        emphasis: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        select: {
          label: { show: true, fontSize: 10, color: '#fff' },
          itemStyle: { areaColor: '#f59e0b' },
        },
        label: { show: false },
        itemStyle: {
          areaColor: dark ? '#1e2633' : '#E8ECF4',
          borderColor: dark ? '#374151' : '#fff',
          borderWidth: 0.8,
        },
      }],
    };
  }, [mapReady, regionData, maxRegionVal, dark]);

  const onMapClick = useCallback(e => {
    const region = e?.name;
    if (!region) return;
    setSelectedRegion(prev => prev === region ? null : region);
  }, []);

  if (metaLoading) return <div className="loading-state"><div className="spinner" /> Loading…</div>;

  const trendLoading = trendApi.loading;
  const trendError   = trendApi.error;

  return (
    <div>
      {/* ── Filter bar ─────────────────────────────────────────────── */}
      <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
        <span className="filter-label">Session</span>
        <select className="filter-select" value={sessionType} onChange={e => { setSessionType(e.target.value); setSelectedRegion(null); }}>
          {sessionTypes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {selectedRegion && (
          <button
            onClick={() => setSelectedRegion(null)}
            style={{ fontSize: 12, fontWeight: 600, color: '#EF4444', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: 100, padding: '4px 12px', cursor: 'pointer' }}
          >
            ✕ {selectedRegion}
          </button>
        )}
      </div>

      {/* ── Map + trend grid ────────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: selectedRegion ? '1fr 1fr' : '1fr',
        gap: 16,
        alignItems: 'stretch',   // map card stretches to match the right column height
      }}>

        {/* Choropleth map — fills the row height so it aligns with the right column */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="card-header">
            <div>
              <div className="card-title">Active Users by Region</div>
              <div className="card-sub">{`${effectiveCat} · ${fmtMonth(selectedMonth)} · click a region to see 6-month trend`}</div>
            </div>
          </div>
          <div className="card-body" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {geoError && <ChartError message="Failed to load UK map data" height={520} />}
            {!geoError && (snapshot.loading || !mapReady) && <ChartLoader height={520} />}
            {!geoError && mapReady && !snapshot.loading && snapshot.error && <ChartError message={snapshot.error} height={520} />}
            {!geoError && mapReady && !snapshot.loading && !snapshot.error && mapOption && (
              regionData.length > 0 ? (
                <ReactECharts
                  key={selectedRegion ? 'split' : 'full'}
                  echarts={echarts}
                  option={mapOption}
                  style={selectedRegion ? { height: '100%', minHeight: 420, flex: 1 } : { height: 520 }}
                  onEvents={{ click: onMapClick }}
                  notMerge
                />
              ) : (
                <div className="empty-state" style={{ height: 520 }}>No data for selected filters</div>
              )
            )}
          </div>
        </div>

        {/* Right panel: 6-month trend charts for region's postcodes */}
        {selectedRegion && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ChartCard
              title={`Monthly Active Users — ${selectedRegion}`}
              sub={`Last 6 months ending ${fmtMonth(selectedMonth)} · postcodes in region`}
            >
              {trendLoading && <ChartLoader height={trendHeight} />}
              {trendError   && <ChartError message={trendError} height={trendHeight} />}
              {!trendLoading && !trendError && userSeries.length > 0 && (
                <LineChart categories={chartMonths.map(fmtMonth)} series={userSeries} height={trendHeight} wrapLegend legendRows={trendLegendRows} tooltipOrder="valueDesc" />
              )}
              {!trendLoading && !trendError && userSeries.length === 0 && (
                <div className="empty-state" style={{ height: trendHeight }}>No trend data for this region</div>
              )}
            </ChartCard>

            <ChartCard
              title={`Monthly Sessions — ${selectedRegion}`}
              sub={`Last 6 months ending ${fmtMonth(selectedMonth)} · postcodes in region`}
            >
              {trendLoading && <ChartLoader height={trendHeight} />}
              {trendError   && <ChartError message={trendError} height={trendHeight} />}
              {!trendLoading && !trendError && sessionSeries.length > 0 && (
                <LineChart categories={chartMonths.map(fmtMonth)} series={sessionSeries} height={trendHeight} wrapLegend legendRows={trendLegendRows} tooltipOrder="valueDesc" />
              )}
              {!trendLoading && !trendError && sessionSeries.length === 0 && (
                <div className="empty-state" style={{ height: trendHeight }}>No trend data for this region</div>
              )}
            </ChartCard>
          </div>
        )}
      </div>

      {!selectedRegion && !snapshot.loading && regionData.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>
          Click a region on the map to see the 6-month postcode trend
        </div>
      )}
    </div>
  );
}
